<template>
	<div class="home">
		<VContainer class="d-flex flex-row justify-center mt-5">
			<VCardTitle
				:class="titleCard"
				class="font-weight-bold primary--text text-center text-break"
			>
				{{ $t('views.home.title') }}<br>
				{{ $t('views.home.subTitle') }}
			</VCardTitle>
		</VContainer>
		<VContainer class="d-flex flex-row justify-center mt-5">
			<VRow align-content="center">
				<VCol
					cols="12"
					md="4"
					lg="4"
					xl="4"
				>
					<UserCard
						:content="$t('views.home.card.salarie.content')"
						:title="$t('views.home.card.salarie.title')"
						:user-type="RoleEnum.ASSURE"
					/>
				</VCol>
				<VCol
					cols="12"
					md="4"
					lg="4"
					xl="4"
				>
					<UserCard
						:content="$t('views.home.card.employeur.content')"
						:title="$t('views.home.card.employeur.title')"
						:user-type="RoleEnum.EMPLOYEUR"
					/>
				</VCol>
				<VCol
					cols="12"
					md="4"
					lg="4"
					xl="4"
				>
					<UserCard
						:content="$t('views.home.card.employeurParticulier.content')"
						:title="$t('views.home.card.employeurParticulier.title')"
						:user-type="RoleEnum.EMPLOYEUR_PARTICULIER"
					/>
				</VCol>
				<VCol cols="12">
					<VCard
						class="pa-5 card-temoin"
						flat
					>
						<VRow justify="space-between">
							<VCol
								cols="12"
								md="6"
							>
								<VCardTitle class="text-h5 font-weight-bold primary--text text-center text-break">
									{{ $t('views.home.card.temoin.title') }}
								</VCardTitle>
								<VCardText class="primary--text text-md-body-1">
									{{ $t('views.home.card.temoin.content') }}
								</VCardText>
							</VCol>
							<VCol
								cols="12"
								md="5"
								align-self="center"
							>
								<div class="d-flex pa-5">
									<VForm
										ref="form"
										class="d-flex w-100"
									>
										<VTextField
											v-model="code"
											outlined
											:rules="codeRules"
											placeholder="Saisir votre code"
										/>
										<VBtn
											height="55"
											outlined
											class="ml-3"
											:loading="state === StateEnum.PENDING"
											@click="handleLoginTemoin"
										>
											<VIcon>
												{{ arrowRight }}
											</VIcon>
										</VBtn>
									</VForm>
								</div>
							</VCol>
						</VRow>
					</VCard>
				</VCol>
			</VRow>
		</VContainer>
	</div>
</template>

<script lang="ts">
	import Vue from 'vue';
	import Component from 'vue-class-component';
	import { mapActions, mapState } from 'vuex';
	import UserCard from '@/components/Home';
	import { mdiArchive, mdiArrowRight, mdiEmail, mdiFile } from '@mdi/js';
	import { LocalStorageEnum } from '@/types/enum/LocalStorageEnum';
	import RouteService, { localStorageNameRedirectLoggedOut } from '@/services/RouteService';
	import { NOTIFICATION_PATH } from '@/constants/uri';
	import { RolesEnum } from '@/types/enum/RolesEnum';
	import { required } from '@cnamts/vue-dot/src/rules/required';
	import { StateEnum } from '@cnamts/vue-dot/src/constants/enums/StateEnum';
	import { redirectToUserPage } from '@/router/authentication';

	/** Home page */
	@Component<Home>({
		components: { UserCard },
		computed: mapState('auth', ['user', 'loading']),
		methods: mapActions('auth', [
			'login'
		])
	})
	export default class Home extends Vue {
		userType = '';
		email = mdiEmail;
		file = mdiFile;
		archive = mdiArchive;
		arrowRight = mdiArrowRight;
		RoleEnum = RolesEnum;
		code = '';
		codeRules = [
			required
		];
		StateEnum = StateEnum;
		state: StateEnum = StateEnum.IDLE;

		get titleCard(): string {
			return this.isSmAndDown ? 'text-h4' : 'text-h3';
		}

		get isSmAndDown(): boolean {
			return this.$vuetify.breakpoint.smAndDown;
		}

		created(): void {
			if (this.user) {
				let route = 'dossierAssure';
				if (this.user.type === 'employeur') {
					route = 'dossierEmployeur';
				}
				this.$router.push({
					name: route
				});
			}

			const routeRedirectIfLoggedOut = RouteService.getRedirectToLoggedOut();
			if (routeRedirectIfLoggedOut !== null) {
				RouteService.deleteRedirectTo(localStorageNameRedirectLoggedOut);
				this.$router.push(routeRedirectIfLoggedOut);
			}
		}

		mounted(): void {
			this.showNotification();
			if (localStorage.getItem(LocalStorageEnum.CONFIRMATION_EMAIL_POP_UP)) {
				localStorage.removeItem(LocalStorageEnum.CONFIRMATION_EMAIL_POP_UP);
			}
		}

		showNotification(): void {
			if (this.hasToShowNotification()) {
				//le queryparam 'notification' doit être constitué comme ceci : type.traduction, ex : success.logout.ok
				let notification = this.$route.query.notification as string;
				if (localStorage.getItem(LocalStorageEnum.TOKEN_EXPIRED) === 'true') {
					notification = 'info.logout.expired';
					localStorage.removeItem(LocalStorageEnum.TOKEN_EXPIRED);
				}
				if (localStorage.getItem(LocalStorageEnum.DECONNEXION) === 'true') {
					localStorage.removeItem(LocalStorageEnum.DECONNEXION);
				}
				const type = (notification as string).split('.')[0];
				const messageLabel = notification.slice(notification.indexOf('.'), notification.length);
				const messageExist = this.$te('notification' + messageLabel);
				// On n'affiche pas si le texte ne correspond pas à une traduction gérée
				if (messageExist) {
					const message = this.$t('notification' + messageLabel);
					this.$store.dispatch(NOTIFICATION_PATH, { type, message });
				}
			}
		}

		hasToShowNotification(): boolean|string {
			return (this.$route.query.notification && (localStorage.getItem(LocalStorageEnum.DECONNEXION) === 'true' || localStorage.getItem(LocalStorageEnum.TOKEN_EXPIRED) === 'true'));
		}

		async handleLoginTemoin(): Promise<void> {
			if (this.$refs.form?.validate()) {
				try {
					await this.login({
						username: this.code,
						userType: RolesEnum.TEMOIN
					});
				} catch {
					return;
				}
				await redirectToUserPage();
			}
		}
	}
</script>

<style scoped>
.home {
	max-width: 1150px;
	margin: auto;
}

:deep(.v-text-field__details) {
	display: none;
}

:deep(.v-input__slot) {
	margin: 0;
}

.card-temoin {
	border: solid 1px #CED9EB;
	box-shadow: 0 0 5px 0 #0000000D;
}
</style>
